<template>
    <div class="constrain pt-2 lg:pt-24">
      <h1 class="heading-1 text-center">{{ category }} Near Me</h1>
      <p class="text-center heading-2">{{ regionStore.isPopular() ? 'Popular Locations' : regionStore.currentRegion }}</p>
  
      <!-- Region Selector with Horizontal Scrolling -->
      <div class="flex flex-nowrap md:flex-wrap md:justify-center overflow-x-auto w-full md:max-w-[1000px] mx-auto gap-4 pt-4 lg:pt-16">
          <NuxtLink v-for="group in locationGroups" :key="group.name"
              :to="`/${categorySlug}/${group.link}`"
              :class="[
              'whitespace-nowrap rounded-md py-2 px-6 text-lg font-semibold text-center cursor-pointer',
              'hover:bg-[hsl(262,100%,98%)] hover:text-primary',
              regionStore.isActiveGroup(group.name) ? 'bg-[#F8F4FF] text-primary' : 'text-grey-5'
              ]"
          >
              {{ group.name }}
          </NuxtLink>
      </div>
  
      <div class="grid grid-cols-1 lg:grid-cols-12 mt-10 lg:mt-20"> 
        <div class="col-span-4">
          <NearMeCountyMenu/>
        </div>
        <div class="col-span-8 px-8">
          <NearMeLocationGrid/>
        </div>
      </div>
    </div>

    <div class="py-6 lg:py-24 mt-10 lg:mt-20 px-5 text-center bg-light-green">
      <h3 class="heading-2">Book your skip now</h3>
      <p class="max-w-2xl mx-auto mt-4">
        You can easily hire the right-sized skip for all your waste disposal needs —it's fast and hassle-free!
      </p>
      <BookingPostcode class="w-full max-w-md mx-auto mt-2 mt-10" />
    </div>

    <div class="text-center mt-10 lg:mt-20 mb-10">
      <h4 class="subtitle-1">Need help finding a location?</h4>
      <div class="mt-2 lg:mt-6 text-grey-dark font-normal text-base">
        You can send us message, call or chat live with an expert for assistance.
      </div>
      <Button class="mt-6" as="nuxt-link" to="/contact-us" size="lg">Contact us</Button>
    </div>
  
    <div class="frame z-0 hidden lg:block">
      <div class="circle"></div>
    </div>
  </template>
  
  <script setup>
  const props = defineProps({
    location: String,
    blok: Object,
  });
  
  definePageMeta({
    name: 'near-me'
  });
  
  const route = useRoute();
  const nearMeStore = useNearMeStore();
  const regionStore = useRegionStore();
  const categorySlug = route.params.category;
  
  const category = computed(() => {
    return categorySlug 
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  });
  
  // Fetch regions and set up reactive refs
  await regionStore.fetchRegions();
  const regions = computed(() => regionStore.regions);
  
  // Convert regions to location groups format
  const locationGroups = computed(() => {
    const groups = regions.value.map(region => ({
      name: region.name,
      link: `near-me/${region.name.toLowerCase().replace(/ /g, '-')}`
    }));
    
    groups.unshift({
      name: 'Popular',
      link: 'near-me/popular'
    });

    return groups;
  });

  regionStore.setCurrentLocationSlug(props.location);

  const canonicalUrl = computed(() => {
    const page = route.query.page || 1;
    if (page > 1) {
        return `https://justhire.com${route.path}?page=${page}`;
    }
    if (props.location === 'popular') {
        return `https://justhire.com${route.path.replace('/popular', '')}`;
    }
    return `https://justhire.com${route.path}`;
  });
  
  useHead({
    link: [
      {
        rel: 'canonical',
        href: canonicalUrl
      }
    ]
  });

  const capitalizeLocation = (location) => {
    return location
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const ogImage = computed(() => {
    return 'https://justhire.com/og-image.webp';
  });

  useSeoMeta({
    title: () => {
      const location = route.params.location ? capitalizeLocation(route.params.location) : '';
      const suffix = ' | 2-16 yard skips | JustHire';
      const baseTitle = location ? `Skip Hire Near Me in ${location}` : 'Skip Hire Near Me';
      
      let title = baseTitle + suffix;
      
      if (title.length > 55) {
        title = baseTitle + ' | 2-16 yard skips';
        
        // If still too long, truncate the location and add ellipsis
        if (title.length > 55 && location) {
          title = `Skip Hire Near Me in ${location}`;
        }
      }
      
      return title;
    },
    description: () => {
      const location = route.params.location ? capitalizeLocation(route.params.location) : '';
      return location ? `Need skip hire in ${location}? We supply a comprehensive range of skip sizes from 2 to 16 yards, across the entire UK. Get the best quote now!` : 'Need skip hire near you? We supply a comprehensive range of skip sizes from 2 to 16 yards, across the entire UK. Get the best quote now!';
    },
      // Open Graph
    ogTitle: () => 'Skip Hire Near Me in ' + location,
    ogDescription: () => 'Need skip hire in ' + location + '? We supply a comprehensive range of skip sizes from 2 to 16 yards, across the entire UK. Get the best quote now!',
    ogImage: () => ogImage.value,
    ogUrl: () => canonicalUrl.value,
    ogType: 'website',
    ogSiteName: 'JUSTHIRE',
    // Twitter
    twitterTitle: () => 'Skip Hire Near Me in ' + location,
    twitterDescription: () => 'Need skip hire in ' + location + '? We supply a comprehensive range of skip sizes from 2 to 16 yards, across the entire UK. Get the best quote now!',
    twitterImage: () => ogImage.value,
    twitterCard: 'summary_large_image',
    twitterSite: '@justhire',
  });

  </script>
  
  <style scoped>
  .frame {
    position: absolute;
    z-index:-1;
    left: -300px;
    top: 400px;
    width: 1200px;
    height: 1200px;
    margin: 50px auto;
    overflow: hidden;
  }
  
  .circle {
    position: relative;
    top:100px;
    width: 945px;
    height: 945px;
    border-radius: 50%;
    background:
      radial-gradient(circle,
      rgba(95, 20, 225, 0.5) 0%,
      rgba(95, 20, 225, 0.35) 40%,
      rgba(95, 20, 225, 0.15) 70%,
      rgba(95, 20, 225, 0) 100%
      );
    filter: blur(100px);
    opacity: 0.2;
  }
  
  @media (max-width: 1024px) {
    .frame {
      width: 300px;
      height: 300px;
      margin: 30px auto;
    }
  
    .circle {
      top: 80px;
      left: 80px;
      width: 300px;
      height: 300px;
    }
  }
  
  @media (max-width: 768px) {
    .frame {
      width: 200px;
      height: 200px;
      margin: 10px auto;
    }
  }
  </style> 